import { useCallback } from 'react';
import { EditorSection } from '@/components/plot/editor/EditorSection';
import { FigurePanel, PanelAnnotation, useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Type, ArrowRight, Brackets, Edit2, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface PanelAnnotationsTabProps {
  panel: FigurePanel;
}

export function PanelAnnotationsTab({ panel }: PanelAnnotationsTabProps) {
  const addAnnotation = useFigureCompositionStore((state) => state.addAnnotation);
  const removeAnnotation = useFigureCompositionStore((state) => state.removeAnnotation);
  const setSelectedAnnotationId = useFigureCompositionStore((state) => state.setSelectedAnnotationId);
  const setSelectedPanelId = useFigureCompositionStore((state) => state.setSelectedPanelId);

  const annotations = panel.annotations ?? [];

  const handleAddAnnotation = (type: 'text' | 'arrow' | 'bracket') => {
    try {
      const defaultAnnotation: Omit<PanelAnnotation, 'id'> = {
        type,
        x: 0.5,
        y: 0.2,
        ...(type === 'arrow' || type === 'bracket'
          ? { x2: 0.7, y2: 0.3 }
          : {}),
        ...(type === 'text' || type === 'bracket'
          ? { content: type === 'text' ? 'Text' : 'p<0.05' }
          : {}),
      };
      addAnnotation(panel.id, defaultAnnotation);
    } catch (error) {
      console.error('Failed to add annotation:', error);
    }
  };

  const handleEditAnnotation = useCallback((annotationId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Edit annotation clicked:', annotationId, 'Panel:', panel.id);
    console.log('Available annotations:', annotations.map(a => a.id));
    
    // Verify annotation exists
    const annotation = annotations.find(a => a.id === annotationId);
    if (!annotation) {
      console.error('Annotation not found:', annotationId);
      return;
    }
    
    // Set panel ID first, then annotation ID
    console.log('Setting selectedPanelId to:', panel.id);
    setSelectedPanelId(panel.id);
    
    // Use setTimeout to ensure panel is selected before annotation
    setTimeout(() => {
      console.log('Setting selectedAnnotationId to:', annotationId);
      setSelectedAnnotationId(annotationId);
      
      // Verify state was set
      setTimeout(() => {
        const state = useFigureCompositionStore.getState();
        console.log('Current state - selectedPanelId:', state.selectedPanelId, 'selectedAnnotationId:', state.selectedAnnotationId);
      }, 10);
    }, 10);
    
    setTimeout(() => {
      const propertiesHeader = document.querySelector('[data-properties-panel-header]') as HTMLElement;
      if (propertiesHeader) {
        propertiesHeader.focus();
      }
    }, 50);
  }, [panel.id, annotations, setSelectedAnnotationId, setSelectedPanelId]);

  const handleDeleteAnnotation = (annotationId: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    try {
      removeAnnotation(panel.id, annotationId);
    } catch (error) {
      console.error('Failed to delete annotation:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded border border-dashed">
        <strong>Note:</strong> For freehand drag annotation, make changes in the plot editor.
      </div>
      <EditorSection
        id="panel-annotations"
        title="Panel annotations"
        description="Add text, arrows, or brackets to this panel."
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Add annotation</Label>
            <TooltipProvider>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 text-xs">
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Add text, arrow, or bracket annotations to this panel</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleAddAnnotation('text')}>
                    <Type className="h-4 w-4 mr-2" />
                    Text
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddAnnotation('arrow')}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Arrow
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddAnnotation('bracket')}>
                    <Brackets className="h-4 w-4 mr-2" />
                    Bracket
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipProvider>
          </div>
          {annotations.length === 0 ? (
            <div className="text-xs text-muted-foreground py-2">
              No annotations. Click "Add" to create one.
            </div>
          ) : (
            <div className="space-y-1">
              {annotations.map((annotation) => (
                <div
                  key={annotation.id}
                  className="flex items-center justify-between p-2 rounded border hover:bg-accent transition-colors"
                  onClick={(e) => {
                    // Only handle row click if clicking on the row itself, not buttons
                    if ((e.target as HTMLElement).closest('button')) {
                      return; // Let button handle its own click
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditAnnotation(annotation.id, e);
                  }}
                >
                  <div 
                    className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  >
                    {annotation.type === 'text' && <Type className="h-3 w-3 text-muted-foreground flex-shrink-0 pointer-events-none" />}
                    {annotation.type === 'arrow' && <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0 pointer-events-none" />}
                    {annotation.type === 'bracket' && <Brackets className="h-3 w-3 text-muted-foreground flex-shrink-0 pointer-events-none" />}
                    <span className="text-xs truncate pointer-events-none">
                      {annotation.content || `${annotation.type} (${annotation.x.toFixed(2)}, ${annotation.y.toFixed(2)})`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Edit button clicked directly:', annotation.id);
                        handleEditAnnotation(annotation.id, e);
                      }}
                      title="Edit annotation"
                      aria-label="Edit annotation"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={(e) => handleDeleteAnnotation(annotation.id, e)}
                            title="Delete annotation"
                            aria-label="Delete annotation"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Delete this annotation</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </EditorSection>
    </div>
  );
}

